/**
 * Copyright SimVentions, Inc. Usage, distribution, transferal, and licensing
 * of this source code is protected under SBIR law as described in DFARS 252.227-7018.
 *
 * SBIR data rights fully described in the README.md file in the top level directory of this project.
 */
import * as React from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "baseui/modal";
import { Pagination } from "baseui/pagination";
import { ListItem, ListItemLabel } from "baseui/list";
import { Block } from "baseui/block";
import { useDexterTheme } from "../DesignSystem/Theme/ThemeContext";
import { ProjectStorableState } from "../ProjectState/ProjectModels";
import { ProjectContext } from "../UserApp";
import { BiX } from "react-icons/bi";
import { DeleteConfirmationModal } from "./DeleteConfirmationModal";

export interface LoadMappingModalProps {
  isOpen: boolean;
  onClose: () => void;
  mappings: Array<{
    projectID: string;
    projectName: string;
    createdAt: number;
  }>;
  onSelect: (project: ProjectStorableState) => void;
  onDelete: (projectID: string) => void;
  itemsPerPage?: number;
}

export const LoadMappingModal: React.FC<LoadMappingModalProps> = ({
  isOpen,
  onClose,
  mappings,
  onSelect,
  onDelete,
  itemsPerPage = 10,
}) => {
  const { currentTheme } = useDexterTheme();
  const [currentPage, setCurrentPage] = React.useState(1);
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] =
    React.useState(false);
  const [mappingToDelete, setMappingToDelete] = React.useState<{
    projectID: string;
    projectName: string;
  } | null>(null);

  const projectContext = React.useContext(ProjectContext);
  const { projectState } = projectContext;

  // Calculate slice for current page
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const pageItems = mappings.slice(startIndex, endIndex);

  const totalPages = Math.ceil(mappings.length / itemsPerPage);

  function formatTimestamp(timeStamp: number): string {
    const date = new Date(timeStamp);

    return date.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  const handleDeleteClick = (
    event: React.MouseEvent,
    projectID: string,
    projectName: string
  ): void => {
    event.stopPropagation(); // Prevent triggering the list item click
    setMappingToDelete({ projectID, projectName });
    setIsDeleteConfirmationOpen(true);
  };

  const confirmDelete = (): void => {
    if (mappingToDelete) {
      onDelete(mappingToDelete.projectID);
      setIsDeleteConfirmationOpen(false);
      setMappingToDelete(null);
    }
  };

  const cancelDelete = (): void => {
    setIsDeleteConfirmationOpen(false);
    setMappingToDelete(null);
  };

  React.useEffect(() => {
    // Reset page to 1 when mappings change or modal opens
    if (isOpen) setCurrentPage(1);
  }, [mappings, isOpen]);

  return (
    <>
      <Modal
        onClose={onClose}
        isOpen={isOpen}
        closeable
        animate
        autoFocus
        overrides={{
          Dialog: {
            style: {
              backgroundColor: currentTheme.colors.pageBackgroundColor,
            },
          },
          Close: {
            style: {
              color: currentTheme.colors.text,
            },
          },
        }}
      >
        <ModalHeader
          style={{
            color: currentTheme.colors.fileModalHeaderTextColor,
            fontFamily: currentTheme.fonts.primary,
          }}
        >
          Load Saved Mapping
        </ModalHeader>
        <ModalBody style={{ maxHeight: "500px", overflow: "auto" }}>
          {mappings.length === 0 ? (
            <Block padding="scale500">No saved mappings available.</Block>
          ) : (
            <Block maxHeight="500px" overflow="auto">
              {pageItems.map(({ projectID, projectName, createdAt }) => {
                const isCurrentlyOpen = projectID === projectState.projectID;

                return (
                  <div
                    key={projectID}
                    data-testid={`mapping-item-${projectName
                      .replace(/\s+/g, "-")
                      .toLowerCase()}`}
                  >
                    <ListItem
                      key={projectID}
                      onClick={() => {
                        const project = projectState.bufferedProjects.find(
                          (p) => p.projectID === projectID
                        );
                        onSelect(project);
                        onClose();
                      }}
                      overrides={{
                        Root: {
                          style: ({ $isHighlighted }) => ({
                            cursor: "pointer",
                            backgroundColor: $isHighlighted
                              ? currentTheme.colors.backgroundTertiary
                              : currentTheme.colors.pageBackgroundColor,
                            color: currentTheme.colors.text,
                            fontFamily: currentTheme.fonts.primary,
                            ":hover": {
                              backgroundColor:
                                currentTheme.colors.backgroundTertiary,
                            },
                            paddingTop: currentTheme.spacing.md,
                            paddingBottom: currentTheme.spacing.md,
                            paddingLeft: currentTheme.spacing.lg,
                            paddingRight: currentTheme.spacing.lg,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }),
                        },
                        Content: {
                          style: {
                            color: currentTheme.colors.text,
                          },
                        },
                      }}
                    >
                      <ListItemLabel>
                        <div>{projectName}</div>
                        <div
                          style={{
                            fontSize: "0.85em",
                            color: currentTheme.colors.text || "#888",
                            marginTop: 2,
                          }}
                        >
                          {formatTimestamp(createdAt)}
                        </div>
                      </ListItemLabel>
                      <button
                        onClick={(e) => {
                          if (!isCurrentlyOpen) {
                            handleDeleteClick(e, projectID, projectName);
                          } else {
                            e.stopPropagation();
                          }
                        }}
                        disabled={isCurrentlyOpen}
                        style={{
                          background: "transparent",
                          border: "none",
                          cursor: isCurrentlyOpen ? "not-allowed" : "pointer",
                          padding: "4px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          marginLeft: "8px",
                          color: currentTheme.colors.text,
                          transition: "color 0.2s",
                          opacity: isCurrentlyOpen ? 0.35 : 1,
                        }}
                        onMouseEnter={(e) => {
                          if (!isCurrentlyOpen) {
                            e.currentTarget.style.color =
                              currentTheme.colors.negative;
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isCurrentlyOpen) {
                            e.currentTarget.style.color =
                              currentTheme.colors.text;
                          }
                        }}
                        title={
                          isCurrentlyOpen
                            ? "Cannot delete the currently open mapping"
                            : "Delete mapping"
                        }
                      >
                        <BiX size={24} />
                      </button>
                    </ListItem>
                  </div>
                );
              })}
            </Block>
          )}
        </ModalBody>
        {mappings.length > itemsPerPage && (
          <ModalFooter>
            <Pagination
              numPages={totalPages}
              currentPage={currentPage}
              onPageChange={({ nextPage }) => setCurrentPage(nextPage)}
              overrides={{
                Root: {
                  style: { color: currentTheme.colors.text },
                },
                PrevButton: {
                  style: {
                    color: currentTheme.colors.text,
                    ":hover": {
                      backgroundColor:
                        currentTheme.colors.dropdownItemHoverBackground,
                    },
                  },
                },
                NextButton: {
                  style: {
                    color: currentTheme.colors.text,
                    ":hover": {
                      backgroundColor:
                        currentTheme.colors.dropdownItemHoverBackground,
                    },
                  },
                },
                DropdownContainer: {
                  style: {
                    color: currentTheme.colors.text,
                  },
                },
                Select: {
                  props: {
                    overrides: {
                      ControlContainer: {
                        style: {
                          backgroundColor: "transparent",
                          color: currentTheme.colors.text,
                          borderColor: currentTheme.colors.text,
                          ":hover": {
                            backgroundColor:
                              currentTheme.colors.dropdownItemHoverBackground,
                          },
                        },
                      },
                      SingleValue: {
                        style: {
                          color: currentTheme.colors.text,
                        },
                      },
                      Dropdown: {
                        style: {
                          backgroundColor:
                            currentTheme.colors.pageBackgroundColor,
                          color: currentTheme.colors.text,
                        },
                      },
                      DropdownListItem: {
                        style: ({ $isHighlighted }) => ({
                          backgroundColor: $isHighlighted
                            ? currentTheme.colors.dropdownItemHoverBackground
                            : currentTheme.colors.pageBackgroundColor,
                          color: currentTheme.colors.text,
                        }),
                      },
                      Option: {
                        style: {
                          backgroundColor:
                            currentTheme.colors.pageBackgroundColor,
                          color: currentTheme.colors.text,
                          ":hover": {
                            backgroundColor:
                              currentTheme.colors.dropdownItemHoverBackground,
                          },
                        },
                      },
                      OptionContent: {
                        style: {
                          color: currentTheme.colors.text,
                        },
                      },
                    },
                  },
                },
                MaxLabel: {
                  style: {
                    color: currentTheme.colors.text,
                  },
                },
              }}
            />
          </ModalFooter>
        )}
      </Modal>
      <DeleteConfirmationModal
        isOpen={isDeleteConfirmationOpen}
        header="Delete Mapping"
        bodyText={
          mappingToDelete
            ? `Are you sure you want to delete the mapping "${mappingToDelete.projectName}"? This action cannot be undone.`
            : ""
        }
        onDelete={confirmDelete}
        onCancel={cancelDelete}
      />
    </>
  );
};
